import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// A living sky that morphs dawn -> day -> golden hour -> night with the page scroll.
export default function AmbientSky() {
  const { scrollYProgress } = useScroll();

  const dawn = useTransform(scrollYProgress, [0, 0.07, 0.12], [1, 1, 0]);
  const day = useTransform(scrollYProgress, [0.10, 0.16, 0.26, 0.31], [0, 1, 1, 0]);
  const dusk = useTransform(scrollYProgress, [0.28, 0.36, 0.50, 0.58], [0, 1, 1, 0]);
  const night = useTransform(scrollYProgress, [0.55, 0.64, 1], [0, 1, 1]);

  // Sun/moon arc
  const bodyX = useTransform(scrollYProgress, [0, 1], ["8%", "88%"]);
  const bodyY = useTransform(scrollYProgress, [0, 0.2, 0.56, 0.78, 1], ["72%", "10%", "68%", "26%", "16%"]);
  const sunOpacity = useTransform(scrollYProgress, [0, 0.05, 0.5, 0.58], [1, 1, 1, 0]);
  const moonOpacity = useTransform(scrollYProgress, [0.58, 0.66], [0, 1]);
  const sunColor = useTransform(
    scrollYProgress,
    [0, 0.2, 0.45],
    ["#F6C86A", "#FFF6DE", "#F4B24A"]
  );
  const starsOpacity = useTransform(scrollYProgress, [0.56, 0.72], [0, 1]);

  const layer = "absolute inset-0 w-full h-full";

  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      <motion.div style={{ opacity: dawn, background: "linear-gradient(180deg,#E4F4E7 0%,#F1F7EE 55%,#DAEEDD 100%)" }} className={layer} />
      <motion.div style={{ opacity: day, background: "linear-gradient(180deg,#C2E8D6 0%,#E6F3EC 55%,#F1F7EE 100%)" }} className={layer} />
      <motion.div style={{ opacity: dusk, background: "linear-gradient(180deg,#2FBF6E 0%,#0AAF53 42%,#06452E 100%)" }} className={layer} />
      <motion.div style={{ opacity: night, background: "linear-gradient(180deg,#04140E 0%,#06271D 55%,#0B3D2E 100%)" }} className={layer} />

      {/* Stars */}
      <motion.div style={{ opacity: starsOpacity }} className={layer}>
        {STARS.map((s, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              left: `${s.x}%`, top: `${s.y}%`, width: s.r, height: s.r,
              animation: `twinkle ${s.d}s ease-in-out ${s.delay}s infinite`,
            }}
          />
        ))}
      </motion.div>

      {/* Sun */}
      <motion.div style={{ left: bodyX, top: bodyY, opacity: sunOpacity, x: "-50%", y: "-50%" }} className="absolute">
        <motion.div
          style={{ background: sunColor }}
          className="w-28 h-28 md:w-40 md:h-40 rounded-full blur-[2px]"
        />
        <div className="absolute inset-0 rounded-full" style={{ boxShadow: "0 0 120px 60px rgba(245,166,35,0.35)" }} />
      </motion.div>

      {/* Moon */}
      <motion.div style={{ left: bodyX, top: bodyY, opacity: moonOpacity, x: "-50%", y: "-50%" }} className="absolute">
        <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-[#E8EAF6]" style={{ boxShadow: "0 0 80px 30px rgba(200,210,255,0.25), inset -12px -8px 0 0 rgba(11,14,36,0.35)" }} />
      </motion.div>
    </div>
  );
}

const STARS = Array.from({ length: 60 }).map(() => ({
  x: Math.random() * 100,
  y: Math.random() * 70,
  r: Math.random() * 2.5 + 1,
  d: Math.random() * 3 + 2,
  delay: Math.random() * 3,
}));
